import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { STATUS_STEPS, STATUS_META, COMPANY, SERVICE_FEES, formatGHS } from "@/data/content";
import { toast } from "sonner";
import {
  ArrowLeft, MapPin, Calendar, Upload, FileText, Download, Loader2, Check, CircleDot,
  CreditCard, Smartphone, Building2,
} from "lucide-react";

function Tracker({ status }) {
  if (status === "rejected") {
    return (
      <div className="p-5 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300" data-testid="tracker-rejected">
        This application was marked as rejected. Please contact our team for details.
      </div>
    );
  }
  const currentIdx = STATUS_STEPS.indexOf(status);
  return (
    <div className="flex items-center justify-between" data-testid="status-tracker">
      {STATUS_STEPS.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={s} className="flex-1 flex items-center last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${done || active ? "border-brand-gold" : "border-white/15"} ${done ? "bg-brand-gold text-brand-navy" : active ? "bg-brand-gold/20 text-brand-gold" : "text-white/30"}`}>
                {done ? <Check className="w-4 h-4" /> : active ? <CircleDot className="w-4 h-4" /> : <span className="text-xs">{i + 1}</span>}
              </div>
              <span className={`text-[10px] mt-2 text-center w-20 ${done || active ? "text-brand-gold" : "text-white/30"}`}>{STATUS_META[s].label}</span>
            </div>
            {i < STATUS_STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 mb-6 ${i < currentIdx ? "bg-brand-gold" : "bg-white/15"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function PayNowDialog({ application }) {
  const [open, setOpen] = useState(false);
  const suggestedFee = SERVICE_FEES[application.service_type];
  const [form, setForm] = useState({ method: "Mobile Money", amount: suggestedFee ? String(suggestedFee) : "", reference: "", note: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("method", form.method);
      fd.append("amount", form.amount);
      fd.append("reference", form.reference);
      fd.append("note", form.note);
      fd.append("application_id", application.id);
      if (file) fd.append("file", file);
      await api.post("/payments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Payment submitted — we'll verify it shortly.");
      setForm({ method: "Mobile Money", amount: suggestedFee ? String(suggestedFee) : "", reference: "", note: "" });
      setFile(null);
      setOpen(false);
    } catch (err) {
      toast.error("Could not submit payment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="pay-now-btn" className="rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">
          <CreditCard className="w-4 h-4 mr-2" /> Pay Now
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-brand-surface border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Pay for {application.title}</DialogTitle></DialogHeader>
        {suggestedFee && (
          <div className="flex items-center justify-between p-3 rounded-lg bg-brand-gold/10 border border-brand-gold/30 mb-1" data-testid="paynow-suggested-fee">
            <span className="text-sm text-white/70">Suggested fee</span>
            <span className="font-display text-lg text-brand-gold">{formatGHS(suggestedFee)}</span>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3 mb-1">
          <div className="p-3 rounded-lg bg-brand-navy border border-white/10">
            <div className="flex items-center gap-2 text-brand-gold text-xs mb-1"><Smartphone className="w-4 h-4" /> Mobile Money</div>
            <p className="font-display text-brand-gold" data-testid="paynow-momo">{COMPANY.momo}</p>
          </div>
          {COMPANY.banks.map((b) => (
            <div key={b.account} className="p-3 rounded-lg bg-brand-navy border border-white/10">
              <div className="flex items-center gap-2 text-brand-gold text-xs mb-1"><Building2 className="w-4 h-4" /> {b.name}</div>
              <p className="font-display text-brand-gold">{b.account}</p>
            </div>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="paynow-method-select"
              className="h-11 rounded-md bg-brand-navy border border-white/10 text-white px-3 text-sm">
              <option>Mobile Money</option>
              <option>Bank Transfer</option>
            </select>
            <Input placeholder="Amount (GHS)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required
              data-testid="paynow-amount-input" className="bg-brand-navy border-white/10 text-white h-11" />
          </div>
          <Input placeholder="Transaction reference / ID" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
            data-testid="paynow-reference-input" className="bg-brand-navy border-white/10 text-white h-11" />
          <label className="flex items-center gap-3 p-3 rounded-md bg-brand-navy border border-dashed border-white/15 cursor-pointer text-sm text-white/60 hover:border-brand-gold/40">
            <Upload className="w-4 h-4 text-brand-gold" />
            {file ? file.name : "Attach payment screenshot (optional)"}
            <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files[0])} data-testid="paynow-file-input" />
          </label>
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="paynow-submit-btn" className="rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [a, d] = await Promise.all([api.get(`/applications/${id}`), api.get(`/applications/${id}/documents`)]);
      setApp(a.data);
      setDocs(d.data);
    } catch (err) {
      toast.error("Could not load application.");
      navigate("/dashboard");
    }
  };

  useEffect(() => { load(); }, [id]);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "document");
      await api.post(`/applications/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded.");
      await load();
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!app) return (
    <div className="min-h-screen bg-brand-navy flex items-center justify-center"><Loader2 className="w-6 h-6 text-brand-gold animate-spin" /></div>
  );

  return (
    <div className="min-h-screen bg-brand-navy text-brand-text">
      <Navbar variant="app" />
      <main className="max-w-4xl mx-auto px-6 pt-28 pb-20">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-white/50 hover:text-brand-gold mb-8 text-sm" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <span className="text-brand-gold text-xs uppercase tracking-wide">{app.service_type === "visa" ? "Visa Application" : "Tour Booking"}</span>
            <h1 className="font-display text-4xl tracking-tight mt-1">{app.title}</h1>
            <div className="flex flex-wrap gap-4 mt-3 text-white/50 text-sm">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {app.destination}</span>
              {app.travel_date && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {app.travel_date}</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <StatusBadge status={app.status} testid="detail-status-badge" />
            {!["rejected", "completed"].includes(app.status) && <PayNowDialog application={app} />}
          </div>
        </div>

        <div className="p-8 rounded-2xl bg-brand-surface border border-white/10 mb-6">
          <h2 className="font-display text-lg mb-8">Application Progress</h2>
          <Tracker status={app.status} />
        </div>

        {app.details && (
          <div className="p-6 rounded-2xl bg-brand-surface border border-white/10 mb-6">
            <h3 className="font-display text-lg mb-2">Details</h3>
            <p className="text-white/60 text-sm leading-relaxed">{app.details}</p>
          </div>
        )}

        {/* DOCUMENTS */}
        <div className="p-6 rounded-2xl bg-brand-surface border border-white/10 mb-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-lg">Documents</h3>
            <label className="cursor-pointer">
              <Button asChild disabled={uploading} className="rounded-full bg-brand-gold text-brand-navy hover:bg-brand-goldhover font-semibold pointer-events-none">
                <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Upload</>}</span>
              </Button>
              <input ref={fileRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx" onChange={upload} data-testid="document-upload-input" />
            </label>
          </div>
          {docs.length === 0 ? (
            <p className="text-white/40 text-sm">No documents uploaded yet. Upload your passport, photos or supporting files.</p>
          ) : (
            <div className="space-y-2">
              {docs.map((d) => (
                <a key={d.id} href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" data-testid={`document-${d.id}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-brand-navy border border-white/10 hover:border-brand-gold/40 transition-colors group">
                  <span className="flex items-center gap-3 text-sm">
                    <FileText className="w-4 h-4 text-brand-gold" />
                    <span>{d.original_filename}</span>
                    {d.doc_type === "payment_proof" && <span className="text-xs text-brand-gold">(payment)</span>}
                  </span>
                  <Download className="w-4 h-4 text-white/40 group-hover:text-brand-gold" />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* TIMELINE */}
        <div className="p-6 rounded-2xl bg-brand-surface border border-white/10">
          <h3 className="font-display text-lg mb-5">Status History</h3>
          <div className="space-y-4">
            {[...app.status_history].reverse().map((h, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-brand-gold mt-1.5" />
                  {i < app.status_history.length - 1 && <div className="w-px flex-1 bg-white/10 my-1" />}
                </div>
                <div className="pb-2">
                  <p className="text-sm font-medium">{STATUS_META[h.status]?.label || h.status}</p>
                  {h.note && <p className="text-white/50 text-sm">{h.note}</p>}
                  <p className="text-white/30 text-xs mt-0.5">{new Date(h.at).toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
